import { useEffect, useState } from "react";
import styled from "styled-components";
import { Download } from "lucide-react";

import Panel from "../../../../components/PartnerDashboard/Panel";
import Skeleton from "../../../../components/PartnerDashboard/Skeleton";
import InfoTip from "../../../../components/PartnerDashboard/InfoTip";
import {
  Button,
  Empty,
  ErrorText,
  Note,
  PageStack,
  PrimaryText,
  SearchInput,
  SecondaryText,
  TableScroll,
  Td,
  Th,
  Toolbar,
} from "../../../../components/PartnerDashboard/ui";
import { adminGetPayoutReport } from "../../../../api/apiEndpoints";
import { formatCurrency } from "../../../../utils/partnerMetrics";
import { downloadCsv } from "../../../../utils/reportDates";
import RangePicker, { useRange } from "../RangePicker";
import { ActionRow, Metric, MetricGrid, MetricLabel, MetricValue, StackTable } from "../ui";

interface StudioPayout {
  businessId: string;
  name: string | null;
  ownerEmail: string | null;
  ownerName: string | null;
  members: number;
  trialPeriods: number;
  upgradePeriods: number;
  compedPeriods: number;
  gross: number;
  membershipPayout: number;
  jrShare: number;
  regularNoShows: number;
  regularLateCancels: number;
  dropNoShows: number;
  dropLateCancels: number;
  uncollectedNoShows: number;
  fees: number;
  totalPayout: number;
  awaitingCheckIn: number;
  awaitingPotential: number;
}

interface MembershipLine {
  businessId: string;
  studio: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phone: string | null;
  product: string;
  preTaxCost: number;
  totalPaid: number;
  startedAt: string;
  expiresAt: string;
  isOnboarded: boolean;
  sharePct: number;
  payout: number;
}

interface FeeLine {
  businessId: string;
  studio: string | null;
  memberName: string | null;
  email: string | null;
  classStartTime: string;
  type: "regular" | "drop";
  status: string;
  charged: boolean;
  fee: number;
}

interface Collected {
  checkedIn: { periods: number; paidCents: number };
  awaiting: { periods: number; paidCents: number; potentialCents: number };
  internal: { periods: number; paidCents: number };
}

interface PayoutReport {
  studios: StudioPayout[];
  lines: MembershipLine[];
  feeLines: FeeLine[];
  collected: Collected;
}

const STUDIOS_PER_PAGE = 15;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  font-family: "Aileron", "Poppins", sans-serif;
  font-size: 13px;
  color: #6b6259;
`;

const MONEY_COLORS = {
  checkedIn: "#2a78d6",
  awaiting: "#eda100",
  internal: "#7d746b",
};

const Bar = styled.div`
  display: flex;
  gap: 2px;
  height: 14px;
  border-radius: 4px;
  overflow: hidden;
  margin: 4px 0 16px;
  background: #f0ebe4;
`;

const Segment = styled.div<{ $color: string; $pct: number }>`
  background: ${({ $color }) => $color};
  flex: 0 0 ${({ $pct }) => $pct}%;
`;

const MoneyRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 0.8fr) minmax(0, 1.6fr);
  gap: 12px;
  align-items: baseline;
  padding: 10px 0;
  border-top: 1px solid #f3eee7;
  font-family: "Aileron", "Poppins", sans-serif;
  font-size: 13.5px;
  color: #2a241f;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 2px;
  }
`;

const MoneyTotal = styled.div`
  font-family: "Aileron", "Poppins", sans-serif;
  font-size: 13.5px;
  color: #6b6259;

  strong {
    font-size: 22px;
    color: #111111;
    margin-right: 8px;
  }
`;

const Dot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 2px;
  margin-right: 8px;
  background: ${({ $color }) => $color};
`;

const sumCents = (values: number[]) =>
  values.reduce((s, v) => s + Math.round(v * 100), 0) / 100;

const money = (v: number) => v.toFixed(2);

const isoDate = (v: string | null) =>
  v ? new Date(v).toISOString().slice(0, 10) : "";

const feeBreakdown = (s: StudioPayout) =>
  [
    s.regularNoShows &&
      `${s.regularNoShows} no-show${s.regularNoShows === 1 ? "" : "s"}`,
    s.regularLateCancels &&
      `${s.regularLateCancels} late cancel${s.regularLateCancels === 1 ? "" : "s"}`,
    s.dropNoShows &&
      `${s.dropNoShows} drop no-show${s.dropNoShows === 1 ? "" : "s"}`,
    s.dropLateCancels &&
      `${s.dropLateCancels} drop late cancel${s.dropLateCancels === 1 ? "" : "s"}`,
  ]
    .filter(Boolean)
    .join(" · ");

const PayoutsPage = () => {
  const range = useRange("lastMonth");
  const [report, setReport] = useState<PayoutReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (range.invalid) return;
    let cancelled = false;
    setReport(null);
    setError(null);
    setPage(1);
    adminGetPayoutReport(range.range.start, range.endExclusive)
      .then((res) => !cancelled && setReport(res.data))
      .catch(
        () =>
          !cancelled &&
          setError("Couldn't load the payout report. Please try again."),
      );
    return () => {
      cancelled = true;
    };
  }, [range.range.start, range.endExclusive, range.invalid]);

  const studios = report?.studios ?? [];
  const visible = studios
    .filter(
      (s) =>
        !search ||
        (s.name ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (s.ownerEmail ?? "").toLowerCase().includes(search.toLowerCase()),
    )
    .sort(
      (a, b) =>
        b.totalPayout - a.totalPayout ||
        b.awaitingPotential - a.awaitingPotential ||
        (a.name ?? "").localeCompare(b.name ?? "", undefined, {
          sensitivity: "base",
        }),
    );
  const pageCount = Math.max(1, Math.ceil(visible.length / STUDIOS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pageStudios = visible.slice(
    (currentPage - 1) * STUDIOS_PER_PAGE,
    currentPage * STUDIOS_PER_PAGE,
  );
  const totals = {
    total: sumCents(studios.map((s) => s.totalPayout)),
    membership: sumCents(studios.map((s) => s.membershipPayout)),
    fees: sumCents(studios.map((s) => s.fees)),
    jr: sumCents(studios.map((s) => s.jrShare)),
    owed: studios.filter((s) => s.totalPayout > 0).length,
    uncollected: studios.reduce((n, s) => n + s.uncollectedNoShows, 0),
  };
  const fileSuffix = `${range.range.start} to ${range.range.end}`;

  const downloadSummary = () =>
    downloadCsv(
      [
        [
          "Studio",
          "Owner",
          "Owner email",
          "Members",
          "Trial periods",
          "Upgrade periods",
          "Comped periods",
          "Gross",
          "Membership earnings",
          "JR share",
          "No-show fees",
          "Late cancel fees",
          "Drop no-show fees",
          "Drop late cancel fees",
          "Fees",
          "Total payout",
          "Uncollected no-shows",
          "Waiting on check-in",
          "Waiting on check-in, up to",
        ],
        ...studios.map((s) => [
          s.name,
          s.ownerName,
          s.ownerEmail,
          s.members,
          s.trialPeriods,
          s.upgradePeriods,
          s.compedPeriods,
          money(s.gross),
          money(s.membershipPayout),
          money(s.jrShare),
          s.regularNoShows,
          s.regularLateCancels,
          s.dropNoShows,
          s.dropLateCancels,
          money(s.fees),
          money(s.totalPayout),
          s.uncollectedNoShows,
          s.awaitingCheckIn,
          money(s.awaitingPotential),
        ]),
      ],
      `Studio payouts ${fileSuffix}.csv`,
    );

  const searchedIds = new Set(visible.map((s) => s.businessId));
  const inSearch = (row: { businessId: string }) => !search || searchedIds.has(row.businessId);
  const detailSuffix =
    search && visible.length === 1 ? `${visible[0].name ?? "studio"} ${fileSuffix}` : search ? `${search} ${fileSuffix}` : fileSuffix;

  const downloadMembers = () =>
    downloadCsv(
      [
        [
          "First name",
          "Last name",
          "Email",
          "Phone",
          "Gym",
          "Product",
          "Pre-tax cost",
          "Paid",
          "Started",
          "Expires",
          "Checked in",
          "Studio share %",
          "Studio payout",
        ],
        ...(report?.lines ?? []).filter(inSearch).map((l) => [
          l.firstName,
          l.lastName,
          l.email,
          l.phone,
          l.studio,
          l.product,
          money(l.preTaxCost),
          money(l.totalPaid),
          isoDate(l.startedAt),
          isoDate(l.expiresAt),
          l.isOnboarded ? "Yes" : "No",
          l.sharePct,
          money(l.payout),
        ]),
      ],
      `Membership payout detail ${detailSuffix}.csv`,
    );

  const downloadFees = () =>
    downloadCsv(
      [
        [
          "Gym",
          "Member",
          "Email",
          "Class start (UTC)",
          "Booking type",
          "Status",
          "Fee collected",
          "Studio fee",
        ],
        ...(report?.feeLines ?? []).filter(inSearch).map((f) => [
          f.studio,
          f.memberName,
          f.email,
          f.classStartTime,
          f.type === "drop" ? "Class drop" : "Regular",
          f.status,
          f.charged ? "Yes" : "No",
          money(f.fee),
        ]),
      ],
      `Class fee detail ${detailSuffix}.csv`,
    );

  const tiles = [
    {
      label: "Total owed",
      value: formatCurrency(totals.total),
      info: "Membership earnings plus collected class fees.",
    },
    { label: "Membership earnings", value: formatCurrency(totals.membership) },
    {
      label: "Class fees",
      value: formatCurrency(totals.fees),
      info: "Unexcused no-shows ($20.00) and late cancels ($15.00) we collected, regular bookings and class drops.",
    },
    {
      label: "JR share",
      value: formatCurrency(totals.jr),
      info: "Membership pre-tax cost kept by JackedRabbit. Fees are passed through in full.",
    },
    { label: "Studios owed", value: totals.owed.toLocaleString() },
  ];

  return (
    <PageStack>
      <Panel
        title="Payout period"
        info="Same rules as the partner dashboard: a membership billing period pays the studio 50% (trial) or 80% (upgrade) of the plan's full pre-tax price when it started in the period and the studio checked the member in. Internal @jackedrabbitgyms.com accounts are excluded. Class fees count classes that started in the period. Dates are UTC."
        subtitle={range.label}
        action={
          <ActionRow>
            <Button disabled={!report} onClick={downloadSummary}>
              <Download size={15} />
              Summary CSV
            </Button>
            <Button disabled={!report} onClick={downloadMembers}>
              <Download size={15} />
              Member detail
            </Button>
            <Button disabled={!report} onClick={downloadFees}>
              <Download size={15} />
              Fee detail
            </Button>
          </ActionRow>
        }
      >
        <RangePicker state={range} />
        {error && <ErrorText>{error}</ErrorText>}
      </Panel>

      {!error && (
        <>
          <MetricGrid>
            {tiles.map((t) => (
              <Metric key={t.label}>
                {report || error ? (
                  <MetricValue>{report ? t.value : "—"}</MetricValue>
                ) : (
                  <Skeleton $w="60%" $h="24px" $radius="8px" />
                )}
                <MetricLabel>
                  {t.label}
                  {t.info && (
                    <InfoTip text={t.info} label={`About ${t.label}`} />
                  )}
                </MetricLabel>
              </Metric>
            ))}
          </MetricGrid>

          {report?.collected && (
            <Panel
              title="Where the money went"
              subtitle="What members paid for memberships that started in this period"
              info="Amounts are what members actually paid, including tax and card fees, after promos. This won't match Stripe exactly: Stripe also counts refunds, gym-switch charges and class fees, groups by payment date rather than when a period started, and leaves out invoices paid with account credit."
            >
              {(() => {
                const c = report.collected;
                const total =
                  c.checkedIn.paidCents +
                  c.awaiting.paidCents +
                  c.internal.paidCents;
                const pct = (v: number) => (total ? (v / total) * 100 : 0);
                const rows = [
                  {
                    key: "checkedIn" as const,
                    label: "Checked in",
                    periods: c.checkedIn.periods,
                    paid: c.checkedIn.paidCents,
                    outcome: `Studios owed ${formatCurrency(totals.membership)}`,
                  },
                  {
                    key: "awaiting" as const,
                    label: "Waiting on check-in",
                    periods: c.awaiting.periods,
                    paid: c.awaiting.paidCents,
                    outcome: `Owed $0.00 for now, up to ${formatCurrency(c.awaiting.potentialCents / 100)} if studios check them in`,
                  },
                  {
                    key: "internal" as const,
                    label: "Internal accounts",
                    periods: c.internal.periods,
                    paid: c.internal.paidCents,
                    outcome: "Not included",
                  },
                ];
                return (
                  <>
                    <MoneyTotal>
                      <strong>{formatCurrency(total / 100)}</strong>
                      collected across{" "}
                      {(
                        c.checkedIn.periods +
                        c.awaiting.periods +
                        c.internal.periods
                      ).toLocaleString()}{" "}
                      billing periods
                    </MoneyTotal>
                    <Bar
                      role="img"
                      aria-label="Share of collected money by check-in status"
                    >
                      {rows.map((r) =>
                        r.paid > 0 ? (
                          <Segment
                            key={r.key}
                            $color={MONEY_COLORS[r.key]}
                            $pct={pct(r.paid)}
                            title={`${r.label}: ${formatCurrency(r.paid / 100)}`}
                          />
                        ) : null,
                      )}
                    </Bar>
                    {rows.map((r) => (
                      <MoneyRow key={r.key}>
                        <span>
                          <Dot $color={MONEY_COLORS[r.key]} />
                          <strong>{r.label}</strong> ·{" "}
                          {r.periods.toLocaleString()} periods
                        </span>
                        <span style={{ fontWeight: 700 }}>
                          {formatCurrency(r.paid / 100)}
                        </span>
                        <span style={{ color: "#6b6259" }}>{r.outcome}</span>
                      </MoneyRow>
                    ))}
                  </>
                );
              })()}
            </Panel>
          )}

          <Panel title="By studio" subtitle="Sorted by amount owed">
            <Toolbar>
              <SearchInput
                placeholder="Search studio or owner email…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </Toolbar>
            {!report && !error ? (
              <Skeleton $h="160px" $radius="12px" />
            ) : visible.length === 0 ? (
              <Empty>
                {search
                  ? `No studios match "${search}".`
                  : "No payouts in this period."}
              </Empty>
            ) : (
              <TableScroll>
                <StackTable>
                  <thead>
                    <tr>
                      <Th>Studio</Th>
                      <Th $align="right">Trials</Th>
                      <Th $align="right">Upgrades</Th>
                      <Th $align="right">Membership</Th>
                      <Th $align="right">Class fees</Th>
                      <Th $align="right">Total owed</Th>
                      <Th $align="right">Waiting on check-in</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageStudios.map((s) => (
                      <tr key={s.businessId}>
                        <Td>
                          <PrimaryText>
                            {s.name ?? "Unknown studio"}
                          </PrimaryText>
                          <SecondaryText>
                            {s.ownerEmail ?? "No owner on file"}
                          </SecondaryText>
                        </Td>
                        <Td data-label="Trials" $align="right">
                          {s.trialPeriods}
                        </Td>
                        <Td data-label="Upgrades" $align="right">
                          {s.upgradePeriods}
                        </Td>
                        <Td data-label="Membership" $align="right">
                          {formatCurrency(s.membershipPayout)}
                        </Td>
                        <Td
                          data-label="Class fees"
                          $align="right"
                          title={feeBreakdown(s) || undefined}
                        >
                          <div>
                            {formatCurrency(s.fees)}
                            {feeBreakdown(s) && (
                              <SecondaryText>{feeBreakdown(s)}</SecondaryText>
                            )}
                          </div>
                        </Td>
                        <Td data-label="Total owed" $align="right">
                          <PrimaryText>
                            {formatCurrency(s.totalPayout)}
                          </PrimaryText>
                        </Td>
                        <Td
                          data-label="Waiting on check-in"
                          $align="right"
                          $muted={!s.awaitingCheckIn}
                        >
                          {s.awaitingCheckIn ? (
                            <div>
                              {s.awaitingCheckIn} member
                              {s.awaitingCheckIn === 1 ? "" : "s"}
                              <SecondaryText>
                                up to {formatCurrency(s.awaitingPotential)}
                              </SecondaryText>
                            </div>
                          ) : (
                            "—"
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </StackTable>
              </TableScroll>
            )}
            {pageCount > 1 && (
              <Pager>
                <Button
                  disabled={currentPage === 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  ← Prev
                </Button>
                <span>
                  Page {currentPage} of {pageCount} · {visible.length} studios
                </span>
                <Button
                  disabled={currentPage === pageCount}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next →
                </Button>
              </Pager>
            )}
            {totals.uncollected > 0 && (
              <Note>
                {totals.uncollected} no-show
                {totals.uncollected === 1 ? " was" : "s were"} marked but the
                fee wasn't collected (the card failed or attendance couldn't be
                checked). They aren't in the totals; they're listed in the fee
                detail with "Fee collected" set to No.
              </Note>
            )}
          </Panel>
        </>
      )}
    </PageStack>
  );
};

export default PayoutsPage;
